import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    // App
    'app.title': 'Nexfah motoring',
    
    // Navigation
    'nav.home': 'Home',
    'nav.garages': 'Find Garages',
    'nav.appointment': 'Book Appointment',
    'nav.bookings': 'My Bookings',
    'nav.help': 'Help & Support',
    'nav.signIn': 'Login',
    'nav.signOut': 'Logout',
    'nav.profile': 'Profile',
    
    // Home Page
    'home.title': 'MOTs, Servicing & Repairs — All in One Place',
    'home.subtitle': 'Quick and reliable car care that fits your schedule.',
    'home.vehicleReg': 'Vehicle Registration',
    'home.vehicleRegPlaceholder': 'Enter registration (e.g., AB12 CDE)',
    'home.postcode': 'Postcode',
    'home.postcodePlaceholder': 'Enter your postcode',
    'home.findGarages': 'Find Garages',
    'home.features.easy': 'Simple Online Booking',
    'home.features.easyDesc': 'Book MOT tests, services and maintenance in just a few clicks',
    'home.features.local': 'Trusted Local Garages',
    'home.features.localDesc': 'Find approved garages and service centers near you',
    'home.features.instant': 'Instant Confirmation',
    'home.features.instantDesc': 'Get immediate booking confirmation with transparent pricing',
    
    // Garage Page
    'garage.title': 'Choose a Garage',
    'garage.noResults': 'No garages found in your area',
    'garage.distance': '{distance} miles away',
    'garage.price': '£{price}',
    'garage.rating': '{rating} stars',
    'garage.reviews': '{count} reviews',
    'garage.selectGarage': 'Select Garage',
    'garage.viewDetails': 'View Details',
    'garage.facilities': 'Facilities',
    'garage.openingHours': 'Opening Hours',
    
    // Appointment Page
    'appointment.title': 'Choose Date & Time',
    'appointment.selectDate': 'Select Date',
    'appointment.selectTime': 'Select Time',
    'appointment.morning': 'Morning (9:00 - 12:00)',
    'appointment.afternoon': 'Afternoon (13:00 - 17:00)',
    'appointment.notAvailable': 'Not Available',
    'appointment.continue': 'Continue to Summary',
    
    // Summary Page
    'summary.title': 'Booking Summary',
    'summary.vehicle': 'Vehicle',
    'summary.garage': 'Garage',
    'summary.datetime': 'Date & Time',
    'summary.price': 'Price',
    'summary.total': 'Total',
    'summary.proceedToPayment': 'Proceed to Payment',
    
    // Confirmation Page
    'confirmation.title': 'Booking Confirmed',
    'confirmation.reference': 'Reference Number',
    'confirmation.details': 'Booking Details',
    'confirmation.viewBookings': 'View My Bookings',
    'confirmation.backHome': 'Back to Home',
    
    // Bookings Page
    'bookings.title': 'My Bookings',
    'bookings.noBookings': 'You have no bookings yet',
    'bookings.upcoming': 'Upcoming',
    'bookings.past': 'Past',
    'bookings.cancelled': 'Cancelled',
    'bookings.reschedule': 'Reschedule',
    'bookings.cancel': 'Cancel',
    
    // Auth
    'auth.signInTitle': 'Sign in to continue',
    'auth.signInSubtitle': 'Please sign in to book your MOT test',
    'auth.signInWithGoogle': 'Login with Google',
    'auth.signInSuccess': 'Successfully signed in!',
    'auth.signInError': 'Failed to sign in. Please try again.',
    'auth.signOutSuccess': 'Successfully signed out',
    
    // Help Page
    'help.title': 'Help & Support',
    'help.faq': 'Frequently Asked Questions',
    'help.contact': 'Contact Us',
    'help.phone': 'Phone',
    'help.email': 'Email',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.retry': 'Try Again',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.close': 'Close',
    'common.save': 'Save',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    
    // Validation
    'validation.required': 'This field is required',
    'validation.invalidReg': 'Please enter a valid registration number',
    'validation.invalidPostcode': 'Please enter a valid postcode',
  },
  
  cy: {
    // Welsh translations
    'app.title': 'Nexfah motoring',
    'nav.home': 'Cartref',
    'nav.garages': 'Dod o Hyd i Garejis',
    'nav.appointment': 'Archebu Apwyntiad',
    'nav.bookings': 'Fy Archebion',
    'nav.help': 'Cymorth a Chefnogaeth',
    'nav.signIn': 'Mewngofnodi',
    'nav.signOut': 'Allgofnodi',
    'home.title': 'Archebwch Eich Prawf MOT',
    'home.subtitle': 'Archebu MOT cyflym a hawdd ar gyfer eich cerbyd',
    'home.vehicleReg': 'Rhif Cofrestru Cerbyd',
    'home.postcode': 'Cod Post',
    'home.findGarages': 'Dod o Hyd i Garejis',
    'common.loading': 'Llwytho...',
    'common.error': 'Gwall wedi digwydd',
  },
  
  fr: {
    // French translations
    'app.title': 'Nexfah motoring',
    'nav.home': 'Accueil',
    'nav.garages': 'Trouver des Garages',
    'nav.appointment': 'Réserver un Rendez-vous',
    'nav.bookings': 'Mes Réservations',
    'nav.help': 'Aide et Support',
    'nav.signIn': 'Connexion',
    'nav.signOut': 'Déconnexion',
    'home.title': 'Réservez Votre Test CT',
    'home.subtitle': 'Réservation CT rapide et facile pour votre véhicule',
    'home.vehicleReg': 'Immatriculation du Véhicule',
    'home.postcode': 'Code Postal',
    'home.findGarages': 'Trouver des Garages',
    'common.loading': 'Chargement...',
    'common.error': 'Une erreur s\'est produite',
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const t = (key, params = {}) => {
    let translation = translations[language]?.[key] || translations.en[key] || key;
    
    // Replace parameters in translation
    Object.entries(params).forEach(([param, value]) => {
      translation = translation.replace(`{${param}}`, String(value));
    });
    
    return translation;
  };

  const value = {
    language,
    changeLanguage,
    t,
    availableLanguages: [
      { code: 'en', name: 'English', flag: '🇬🇧' },
      { code: 'cy', name: 'Cymraeg', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
    ],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};